// --------------------------------------------------------------
// Created On: 2025-05-08
// Author: Hannah Vaughan
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useMemo, useState } from "react";
import styles from "./DataPage.module.scss";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import userHasPermission from "src/utilities/userHasPermission";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_BOTTOM_LEFT,
  GOOGLE_MAPS_DEFAULT_LAT,
  GOOGLE_MAPS_DEFAULT_LNG,
  GOOGLE_MAPS_DEFAULT_ZOOM,
  GOOGLE_MAPS_HORIZONTAL_BAR,
  GOOGLE_MAPS_MIN_ZOOM,
  GOOGLE_MAPS_ROAD_MAP,
  GOOGLE_MAPS_SATELLITE,
} from "src/constants/googleMaps";
import { UPDATE_SERVICE_HOURS_PERMISSION } from "../../../constants/permissions";
import calculateCenterZoom from "src/utilities/calculateCenterZoom";
import IconTooltip from "src/components/IconTooltip/IconTooltip";

// Page of the Asset Management Wizard that allows the user to optionally select and enter
// service and coordinates updates.
export default function DataPage(props: Props): Component {
  const [center, setCenter] = useState<ShortPoint>({
    lat: GOOGLE_MAPS_DEFAULT_LAT,
    lng: GOOGLE_MAPS_DEFAULT_LNG,
  });
  const [zoom, setZoom] = useState<number>(GOOGLE_MAPS_DEFAULT_ZOOM);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [point, setPoint] = useState<Point>({ latitude: GOOGLE_MAPS_DEFAULT_LAT, longitude: GOOGLE_MAPS_DEFAULT_LNG });
  const assetMapIconPath = useMemo(
    () => getAssetMapIconPath(props.assetMapIconId, props.assetMapIcons),
    [props.assetMapIconId, JSON.stringify(props.assetMapIcons)]
  );
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  // Re-render the map whenever the coordinates change.
  useEffect(() => {
    const tempPoint = {
      latitude: parseFloat(props.latitude),
      longitude: parseFloat(props.longitude),
    };
    setPoint(tempPoint);
    setCenter({
      lat: tempPoint.latitude,
      lng: tempPoint.longitude,
    });

    // Find the new bounding box that fits the marker.
    if (map !== null) {
      const newBounds = calculateCenterZoom([tempPoint], map);
      setCenter(newBounds.center);
      setZoom(newBounds.zoom - 1);
    } else {
      setZoom(GOOGLE_MAPS_DEFAULT_ZOOM);
    }
  }, [props.latitude, props.longitude]);

  // Gets the asset map icon path based on the map marker selected on the Details page.
  function getAssetMapIconPath(assetMapIconId: number | null, assetMapIcons: AssetMapIcon[]): string {
    let newAssetMapIconPath = "/mapMarkers/mapMarkerColors/default.png";
    const assetMapIcon = assetMapIcons.find((icon) => icon.assetMapIconId === assetMapIconId);
    if (assetMapIcon !== undefined) {
      newAssetMapIconPath = `/mapMarkers/mapMarker${assetMapIcon.code}Active.png`;
    }
    return newAssetMapIconPath;
  }

  // Updates the marker's latitude and longitude where the user clicked on the map.
  function handleClickMap(mapProps: google.maps.MapMouseEvent): void {
    if (mapProps.latLng !== undefined && mapProps.latLng !== null) {
      const newLatitude = Number(mapProps.latLng.lat()).toFixed(7);
      const newLongitude = Number(mapProps.latLng.lng()).toFixed(7);
      props.onChangeLatitude(newLatitude);
      props.onChangeLongitude(newLongitude);
      setPoint({
        latitude: parseFloat(newLatitude),
        longitude: parseFloat(newLongitude),
      });
    }
  }

  // Handle load map event.
  function handleLoadMap(map: google.maps.Map): void {
    let tempPoint: Point = {
      latitude: GOOGLE_MAPS_DEFAULT_LAT,
      longitude: GOOGLE_MAPS_DEFAULT_LNG,
    };
    if (props.latitude !== null && props.longitude !== null) {
      tempPoint = {
        latitude: parseFloat(props.latitude),
        longitude: parseFloat(props.longitude),
      };
    }
    setPoint(tempPoint);

    // Recalculate center and zoom of map around the asset's location.
    const newBounds = calculateCenterZoom([tempPoint], map);
    setCenter(newBounds.center);
    setZoom(newBounds.zoom - 1);
    setMap(map);
  }

  return (
    <Fragment>
      <div className="col mx-2">
        <div className="form-check">
          <input
            data-test="update-service-time-checkbox"
            className={`form-check-input ${styles.title} mt-3`}
            type="checkbox"
            id="hoursSinceLastServicedCheckbox"
            checked={props.updateServiceTimeManually}
            onChange={() => props.onChangeUpdateServiceTimeManually()}
            disabled={props.disabled || !userHasPermission([[UPDATE_SERVICE_HOURS_PERMISSION]])}
          />
          <label className={`form-check-label ${styles.title} mt-2`} htmlFor="hoursSinceLastServicedCheckbox">
            Configure &apos;hours since last serviced&apos; settings
          </label>
        </div>
      </div>
      {props.updateServiceTimeManually && (
        <Fragment>
          <div className="col mx-5">
            <label className="font-weight-bold mb-2">
              <span>Attribute used for tracking hours since last serviced</span>
              <IconTooltip
                id="service-attribute-tooltip"
                icon="info-circle"
                message={
                  "Whenever the selected attribute increases, hours since last serviced will " +
                  "increase the same amount."
                }
                color="var(--info-tooltip)"
              />
            </label>
            <select
              data-test="service-attribute-selection"
              className="form-select"
              value={props.serviceAttribute || ""}
              onChange={(e) => props.onChangeServiceAttribute(e.target.value === "" ? null : e.target.value)}
              disabled={props.disabled}
            >
              <option value="">No service attribute</option>
              {props.runtimeServiceAttributeMap !== null &&
                props.runtimeServiceAttributeMap !== undefined &&
                Object.keys(props.runtimeServiceAttributeMap).map((attributeCode) => (
                  <option key={attributeCode} value={attributeCode}>
                    {props.runtimeServiceAttributeMap[attributeCode].name} (hours)
                  </option>
                ))}
            </select>
          </div>
          <div className="col mx-5 mt-3">
            <label className="font-weight-bold mb-2">
              <span>Hours since last serviced</span>
              <IconTooltip
                id="service-time-tooltip"
                icon="info-circle"
                message="Manually set the hours since the asset was last serviced."
                color="var(--info-tooltip)"
              />
            </label>
            <input
              data-test="service-time-input"
              className="form-control mx-auto"
              value={props.serviceTime}
              onChange={(e) => props.onChangeServiceTime(e.target.value)}
              disabled={props.disabled}
            />
          </div>
        </Fragment>
      )}
      <div className="col mx-2 mt-4">
        <div className="form-check">
          <input
            data-test="update-location-checkbox"
            className={`form-check-input ${styles.title} mt-3`}
            type="checkbox"
            id="updateLocationCheckbox"
            checked={props.updateLocationManually}
            onChange={() => props.onChangeUpdateLocationManually()}
            disabled={props.disabled}
          />
          <label className={`${styles.title} form-check-label mt-2`} htmlFor="updateLocationCheckbox">
            <span>Manually set location</span>
            <IconTooltip
              id="update-location-tooltip"
              icon="info-circle"
              message={
                "Manually set the last reported location of the asset. If this asset is actively reporting its " +
                "location, this value will be overwritten the next time the location is reported."
              }
              color="var(--info-tooltip)"
            />
          </label>
        </div>
      </div>
      {props.updateLocationManually && (
        <Fragment>
          <div className="col mx-5">
            <div className="row align-items-center">
              <div className="col-auto">
                <label className="font-weight-bold mb-2">Latitude</label>
                <input
                  data-test="latitude-input"
                  className="form-control"
                  type="text"
                  value={props.latitude}
                  onChange={(e) => props.onChangeLatitude(e.target.value)}
                  disabled={props.disabled}
                />
              </div>
              <div className="col-auto">
                <label className="font-weight-bold m-2">Longitude</label>
                <input
                  data-test="longitude-input"
                  className="form-control"
                  type="text"
                  value={props.longitude}
                  onChange={(e) => props.onChangeLongitude(e.target.value)}
                  disabled={props.disabled}
                />
              </div>
            </div>
          </div>
          {isLoaded && (
            <div className="col m-3 mx-5">
              <GoogleMap
                mapContainerStyle={{ width: "50%", height: "25rem" }}
                center={center}
                zoom={zoom}
                onLoad={(map) => handleLoadMap(map)}
                onUnmount={() => setMap(null)}
                onClick={(e) => handleClickMap(e)}
                options={{
                  fullscreenControl: false,
                  scaleControl: true,
                  mapTypeControl: true,
                  tilt: 0,
                  minZoom: GOOGLE_MAPS_MIN_ZOOM,
                  styles: [
                    {
                      featureType: "poi",
                      elementType: "labels",
                      stylers: [{ visibility: "off" }],
                    },
                  ],
                  mapTypeControlOptions: {
                    position: GOOGLE_MAPS_BOTTOM_LEFT,
                    style: GOOGLE_MAPS_HORIZONTAL_BAR,
                    mapTypeIds: [GOOGLE_MAPS_ROAD_MAP, GOOGLE_MAPS_SATELLITE],
                  },
                  restriction: {
                    latLngBounds: {
                      east: 180,
                      north: 85,
                      south: -85,
                      west: -180,
                    },
                    strictBounds: true,
                  },
                }}
              >
                {/* MarkerF is used here instead of Marker to prevent rendering issues. */}
                <MarkerF
                  position={{
                    lat: point.latitude,
                    lng: point.longitude,
                  }}
                  icon={{
                    url: assetMapIconPath,
                    size: new google.maps.Size(40, 48),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(20, 48),
                    scaledSize: new google.maps.Size(40, 48),
                  }}
                />
              </GoogleMap>
            </div>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}

interface Props {
  runtimeServiceAttributeMap: RuntimeServiceAttributeMap;
  serviceAttribute: string | null;
  serviceTime: string;
  updateServiceTimeManually: boolean;
  updateLocationManually: boolean;
  latitude: string;
  longitude: string;
  assetMapIcons: AssetMapIcon[];
  assetMapIconId: number | null;
  disabled: boolean;
  onChangeServiceAttribute: (serviceAttribute: string | null) => void;
  onChangeServiceTime: (serviceTime: string) => void;
  onChangeUpdateServiceTimeManually: () => void;
  onChangeUpdateLocationManually: () => void;
  onChangeLatitude: (latitude: string) => void;
  onChangeLongitude: (longitude: string) => void;
}

interface AssetMapIcon {
  assetMapIconId: number;
  name: string;
  code: string;
}

interface RuntimeServiceAttributeMap {
  [key: string]: RuntimeServiceAttribute;
}

interface RuntimeServiceAttribute {
  attributeId: number;
  name: string;
  code: string;
}

interface Point {
  latitude: number;
  longitude: number;
}

interface ShortPoint {
  lat: number;
  lng: number;
}
